import { useEffect, useRef } from 'react';

/*
 * The Farwings bird drawn with ~2,000 glowing particles.
 * - On load the particles fly in from all sides and assemble the logo.
 * - They scatter away from the cursor (or finger) and spring back.
 * - Click / tap bursts the bird apart; it rebuilds itself.
 * Pauses when off-screen; reduced-motion visitors get the still logo.
 */
export default function ParticleMark({ src = '/farwings-mark.png', className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0, H = 0, parts = [], raf = 0, visible = true, t = 0, ready = false;
    const mouse = { x: -9999, y: -9999 };
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    let lastAct = performance.now();
    let lastPulse = performance.now();
    const img = new Image();
    img.src = src;

    const build = () => {
      if (!img.complete || !img.naturalWidth) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = c.getBoundingClientRect();
      W = r.width;
      H = r.height;
      if (!W || !H) return;
      c.width = Math.round(W * dpr);
      c.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const size = Math.round(Math.min(W, H) * 0.92);
      const gap = size < 320 ? 4 : size < 480 ? 5 : 6;
      const off = document.createElement('canvas');
      off.width = off.height = size;
      const o = off.getContext('2d', { willReadFrequently: true });
      o.drawImage(img, 0, 0, size, size);
      const d = o.getImageData(0, 0, size, size).data;
      const ox = (W - size) / 2;
      const oy = (H - size) / 2;

      const next = [];
      for (let y = 0; y < size; y += gap) {
        for (let x = 0; x < size; x += gap) {
          const i = (y * size + x) * 4;
          if (d[i + 3] < 150) continue;
          const blue = d[i + 2] > 200 && d[i] < 120;
          const grey = !blue && d[i] < 200;
          next.push({ tx: ox + x, ty: oy + y, col: blue ? '#4FB0E7' : grey ? '#A6A6A6' : '#F2F7FC', glow: blue });
        }
      }
      parts = next.map((p, k) => {
        const old = parts[k];
        const a = Math.random() * Math.PI * 2;
        const far = Math.max(W, H) * (0.6 + Math.random() * 0.6);
        return {
          ...p,
          x: old ? old.x : reduce ? p.tx : W / 2 + Math.cos(a) * far,
          y: old ? old.y : reduce ? p.ty : H / 2 + Math.sin(a) * far,
          vx: 0,
          vy: 0,
          s: gap * (0.45 + Math.random() * 0.25),
          ph: Math.random() * Math.PI * 2,
        };
      });
      ready = true;
      if (reduce) draw();
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        ctx.fillStyle = p.col;
        ctx.globalAlpha = p.glow ? 1 : 0.92;
        ctx.fillRect(p.x, p.y, p.s, p.s);
      }
      ctx.globalAlpha = 1;
    };

    const step = () => {
      t += 0.016;
      // phones have no hover: every few idle seconds a soft wave runs through the bird
      const now = performance.now();
      if (coarse && now - lastAct > 4000 && now - lastPulse > 4500) {
        lastPulse = now;
        const cx = W / 2;
        const cy = H / 2;
        for (const p of parts) {
          const dx = p.x - cx;
          const dy = p.y - cy;
          const d = Math.hypot(dx, dy) || 1;
          const f = 2.5 + Math.random() * 2.5;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }
      }
      const R = Math.min(W, H) * 0.16;
      for (const p of parts) {
        const wx = p.tx + Math.sin(t * 1.3 + p.ph) * 0.8;
        const wy = p.ty + Math.cos(t * 1.1 + p.ph) * 0.8;
        p.vx += (wx - p.x) * 0.04;
        p.vy += (wy - p.y) * 0.04;
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const dist = Math.hypot(dx, dy);
        if (dist < R && dist > 0.1) {
          const f = (1 - dist / R) * 5;
          p.vx += (dx / dist) * f;
          p.vy += (dy / dist) * f;
        }
        p.vx *= 0.84;
        p.vy *= 0.84;
        p.x += p.vx;
        p.y += p.vy;
      }
    };

    const frame = () => {
      if (visible && ready && !document.hidden) {
        step();
        draw();
      }
      raf = requestAnimationFrame(frame);
    };

    const local = (e) => {
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top, inside: e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom };
    };
    const onMove = (e) => {
      lastAct = performance.now();
      const p = local(e);
      mouse.x = p.x;
      mouse.y = p.y;
    };
    const onLeave = () => {
      mouse.x = mouse.y = -9999;
    };
    const onDown = (e) => {
      lastAct = performance.now();
      const p = local(e);
      if (!p.inside) return;
      for (const q of parts) {
        const dx = q.x - p.x;
        const dy = q.y - p.y;
        const dist = Math.hypot(dx, dy) || 1;
        const f = 18 + Math.random() * 22;
        q.vx += (dx / dist) * f;
        q.vy += (dy / dist) * f;
      }
    };

    // touch: the finger pushes particles while dragging or scrolling over the bird
    const onTouch = (e) => {
      const t0 = e.touches[0];
      if (!t0) return;
      lastAct = performance.now();
      const p = local(t0);
      mouse.x = p.x;
      mouse.y = p.y;
    };
    let tt = 0;
    const onTouchEnd = () => {
      clearTimeout(tt);
      tt = setTimeout(onLeave, 250);
    };

    let rt = 0;
    const onResize = () => {
      clearTimeout(rt);
      rt = setTimeout(build, 150);
    };

    img.onload = build;
    if (img.complete) build();
    window.addEventListener('resize', onResize);
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
    });
    io.observe(c);
    if (!reduce) {
      window.addEventListener('pointermove', onMove, { passive: true });
      document.documentElement.addEventListener('pointerleave', onLeave);
      c.addEventListener('pointerdown', onDown);
      c.addEventListener('touchstart', onTouch, { passive: true });
      c.addEventListener('touchmove', onTouch, { passive: true });
      c.addEventListener('touchend', onTouchEnd);
      c.addEventListener('touchcancel', onTouchEnd);
      raf = requestAnimationFrame(frame);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(rt);
      io.disconnect();
      img.onload = null;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeave);
      c.removeEventListener('pointerdown', onDown);
      c.removeEventListener('touchstart', onTouch);
      c.removeEventListener('touchmove', onTouch);
      c.removeEventListener('touchend', onTouchEnd);
      c.removeEventListener('touchcancel', onTouchEnd);
      clearTimeout(tt);
    };
  }, [src]);

  return <canvas ref={ref} aria-hidden="true" className={`block h-full w-full cursor-pointer touch-pan-y ${className}`} />;
}
